<template>
  <BaseExpansionPanel
    bold-label
    default-opened
  >
    <template #label="{ expanded }">
      <div class="tw:flex tw:justify-between">
        <div class="tw:font-bold">
          {{ getLocaleText("stream_filters_media_locations.location") }}
        </div>

        <div v-if="expanded">
          <a
            v-if="selectedLocationIds.length"
            class="tw:cursor-pointer tw:text-denim-400 tw:hover:text-denim-700"
            data-testid="clear-all"
            @click.stop="clearAll"
          >
            {{ getLocaleText("stream_filters_content.clear_all") }}
          </a>
        </div>
      </div>
    </template>
    <div class="tw:flex tw:flex-col tw:gap-2">
      <div>
        <SourceFiltersSelector
          ref="locationsSelector"
          data-testid="source-filters-selector"
          :filter="loadLocations"
          :model-value="selectedLocationIds"
          :placeholder="
            getLocaleText('stream_filters_media_locations.search_location')
          "
          @item="addLocation"
          @query="updateLocationFilter"
          @visible="locationSelectorVisible"
        >
          <template
            v-if="lastParentLocation"
            #beforeOptions
          >
            <div
              class="tw:flex tw:flex-1 tw:cursor-pointer tw:select-none tw:gap-2 tw:p-2 tw:hover:bg-denim-100"
              :data-testid="`option-${lastParentLocation.id}`"
              @click="addLocation(lastParentLocation, true)"
            >
              <div class="tw:flex tw:items-center">
                <BaseButton
                  circle
                  color="secondary"
                  data-testid="parent-prev"
                  flat
                  icon="chevron-left"
                  new-design
                  new-icon
                  size="sm"
                  @click.stop="selectParentLocation(lastParentLocation)"
                />
              </div>
              <div
                class="tw:flex tw:flex-1 tw:flex-col tw:gap-1 tw:overflow-hidden"
              >
                <div>
                  {{ lastParentLocation.name }}
                </div>
                <div class="tw:text-xs tw:text-denim-600">
                  {{ locationType(lastParentLocation) }}
                </div>
              </div>
            </div>
          </template>
          <template #option="{ option }">
            <div
              class="tw:flex tw:flex-1 tw:cursor-pointer tw:gap-2"
              :data-testid="`option-${option.id}`"
            >
              <div
                class="tw:flex tw:flex-1 tw:flex-col tw:gap-1 tw:overflow-hidden"
              >
                <div class="tw:flex tw:gap-2">
                  <div>
                    {{ option.name }}
                  </div>
                  <div
                    v-if="lastParentLocation || locationFilter"
                    class="tw:text-xs tw:text-denim-600"
                  >
                    {{ locationType(option) }}
                  </div>
                </div>
                <div
                  v-if="option.parents && option.parents.length"
                  class="tw:text-xs tw:text-denim-600"
                >
                  <EllipseContent>
                    <template
                      v-for="(
                        parentLocation, parentLocationKey
                      ) in option.parents"
                      :key="parentLocation.id"
                    >
                      <span v-if="parentLocationKey"> > </span>
                      <span>{{ parentLocation.name }}</span>
                    </template>
                  </EllipseContent>
                </div>
                <div
                  v-else
                  class="tw:text-xs tw:text-denim-600"
                >
                  {{ locationType(option) }}
                </div>
              </div>
              <div
                v-if="option.type !== 'City'"
                class="tw:flex tw:items-center"
              >
                <BaseButton
                  circle
                  color="secondary"
                  data-testid="parent-next"
                  flat
                  icon="chevron-right"
                  new-design
                  new-icon
                  size="sm"
                  @click.stop="selectChildLocation(option)"
                />
              </div>
            </div>
          </template>
        </SourceFiltersSelector>
      </div>

      <div
        v-if="selectedLocations.length"
        class="tw:flex tw:flex-wrap tw:gap-1"
        data-testid="tags"
      >
        <Tag
          v-for="(selectedLocation, selectedLocationIndex) in selectedLocations"
          :key="selectedLocationIndex"
          color="secondary"
          :data-testid="`tag-${selectedLocationIndex}`"
          hide-cursor
          :label="selectedLocation.name as string"
          rounded
          @remove="removeLocation(selectedLocation)"
        >
          <span>{{ selectedLocation.name }}</span>
          <span class="tw:text-xs tw:text-denim-600">
            {{ selectedLocation.type }}
          </span>
        </Tag>
      </div>
    </div>
  </BaseExpansionPanel>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { computed, ref, watch } from "vue";
import { type ComponentExposed } from "vue-component-type-helpers";

import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import { BaseExpansionPanel, Tag } from "shared/components/base";
import BaseButton from "shared/components/base/BaseButton.vue";
import EllipseContent from "shared/components/base/EllipseContent.vue";
import SourceFiltersSelector, {
  type SourceFiltersSelectorFilterResponse,
} from "shared/components/sources/SourceFiltersSelector.vue";
import useLocations from "shared/composables/useLocations";
import { type MediumField } from "shared/helpers/media";
import type { Location as LocationResource } from "shared/resources";
import { type ConvertKeysToSnakeCase, type Nullable } from "shared/types";

interface StreamFiltersMediaProps {
  medium: MediumField;
}

interface Location
  extends ConvertKeysToSnakeCase<Omit<LocationResource, "id">> {
  id: number;
  parents?: Location[];
}

type LocationResponse = SourceFiltersSelectorFilterResponse<Location>;

const { topLocations } = useLocations();

const locationKeys = defineModel<string[]>("locationKeys", {
  default: () => [],
});

const props = defineProps<StreamFiltersMediaProps>();

const locationFilter = ref<string>("");

const locationsSelector = ref<
  ComponentExposed<typeof SourceFiltersSelector> | undefined
>();

const parentLocationIdFilter = ref<Nullable<number>>(null);
const parentLocations = ref<Location[]>([]);

const lastParentLocation = computed<Location | undefined>(() =>
  parentLocations.value.at(-1)
);

const selectedLocations = ref<Location[]>([]);

const selectedLocationIds = computed<number[]>(() =>
  locationKeys.value.flatMap((locationKey) =>
    locationKey.includes(props.medium)
      ? [Number(locationKey.replace(`${props.medium}_`, ""))]
      : []
  )
);

async function loadLocationsById() {
  if (!selectedLocationIds.value.length) {
    selectedLocations.value = [];

    return;
  }

  const result = await $streemApiV1.get("locations", {
    params: {
      ids: selectedLocationIds.value,
    },
  });

  selectedLocations.value = result.data;
}

const debounceLoadLocationsById = debounce(loadLocationsById, 300);

function removeLocation(location: Location) {
  locationKeys.value = locationKeys.value.filter(
    (locationKey) => locationKey !== `${props.medium}_${location.id}`
  );
}

async function loadLocations(index: number): Promise<LocationResponse> {
  if (!locationFilter.value && !parentLocationIdFilter.value) {
    if (index) {
      return { items: [], pages: 1 };
    }

    return { items: topLocations.value, pages: 1 };
  }

  const params: Record<string, unknown> = {
    page: index + 1,
    limit: 50,
    query: locationFilter.value,
    include_parents: true,
  };

  if (parentLocationIdFilter.value) {
    params.parent_id = parentLocationIdFilter.value;
  }

  const result = await $streemApiV1.get<Location[]>("locations", {
    params,
  });

  const { data, headers } = result;

  return {
    items: data,
    pages: Number(headers["x-total-pages"]),
  };
}

function addLocation(selectedLocation: Location, parent?: boolean) {
  locationKeys.value = locationKeys.value.concat(
    `${props.medium}_${selectedLocation.id}`
  );

  if (parent) {
    selectParentLocation(selectedLocation);
  }
}

function selectParentLocation(parentLocation: Location) {
  const parentLocationIndex = parentLocations.value.indexOf(parentLocation);

  parentLocations.value = parentLocations.value.slice(0, parentLocationIndex);

  parentLocationIdFilter.value = parentLocations.value.length
    ? parentLocation.parent_id
    : null;

  if (locationsSelector.value) {
    locationsSelector.value.search();
  }
}

function selectChildLocation(parentLocation: Location) {
  parentLocations.value.push(parentLocation);
  parentLocationIdFilter.value = parentLocation.id;

  if (locationsSelector.value) {
    locationsSelector.value.search();
  }
}

function locationSelectorVisible(visible: boolean) {
  if (!visible) {
    parentLocationIdFilter.value = null;
    parentLocations.value = [];
  }
}

function locationType(location: Location): string {
  if (!location.country_code && location.type === "Country") {
    return getLocaleText("stream_filters_media_locations.continent");
  }

  return location.type;
}

function updateLocationFilter(query: string): void {
  locationFilter.value = query;

  if (!query) {
    locationsSelector.value?.search();
  }
}

function clearAll() {
  locationKeys.value = locationKeys.value.filter(
    (locationKey) => !locationKey.includes(props.medium)
  );
}

watch(
  () => props.medium,
  () => {
    selectedLocations.value = [];
    debounceLoadLocationsById();
  }
);

watch(
  locationKeys,
  () => {
    debounceLoadLocationsById();
  },
  { immediate: true }
);
</script>
